// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// types
import type { TGridSizes } from './types'

/** Default spacing between grid items in pixels. */
export const DEFAULT_GRID_SPACING_PX = 8

/** Resolves the final grid spacing from multiple optional values. */
export const resolveGridSpacing = (...spacing: (number | undefined)[]) => {
  const finalSpacing = spacing.filter((value) => typeof value === 'number')
  if (finalSpacing.length === 0) return DEFAULT_GRID_SPACING_PX
  return Math.max(0, ...finalSpacing.map((value) => value ?? DEFAULT_GRID_SPACING_PX))
}

/** Default scrolling threshold in pixels for triggering near-end events. */
export const DEFAULT_GRID_SCROLLING_THRESHOLD_PX = 80

/** Resolves the final scrolling threshold from multiple optional values. */
export const resolveGridScrollingThreshold = (...threshold: (number | undefined)[]) => {
  const finalThreshold = threshold.filter((value) => typeof value === 'number')
  if (finalThreshold.length === 0) return DEFAULT_GRID_SCROLLING_THRESHOLD_PX
  return Math.max(0, ...finalThreshold.map((value) => value ?? DEFAULT_GRID_SCROLLING_THRESHOLD_PX))
}

/** Default near-end threshold in pixels for triggering near-end events. */
export const DEFAULT_GRID_NEAR_END_THRESHOLD_PX = 80

/** Resolves the final near-end threshold from multiple optional values. */
export const resolveGridNearEndThreshold = (...threshold: (number | undefined)[]) => {
  const finalThreshold = threshold.filter((value) => typeof value === 'number')
  if (finalThreshold.length === 0) return DEFAULT_GRID_NEAR_END_THRESHOLD_PX
  return Math.max(0, ...finalThreshold.map((value) => value ?? DEFAULT_GRID_NEAR_END_THRESHOLD_PX))
}

/** Default grid item height estimate in pixels, used for virtualization calculations. */
export const DEFAULT_GRID_ITEM_HEIGHT_ESTIMATE = 120

/** Default grid item sizes for each breakpoint. */
export const DEFAULT_GRID_ITEM_SIZES: TGridSizes = { xs: 12, sm: 6, md: 4, lg: 3 }

/** Returns the grid item sizes, falling back to defaults if not provided. */
export const getGridItemSizes = (sizes?: TGridSizes): TGridSizes => {
  return sizes ?? DEFAULT_GRID_ITEM_SIZES
}

/** Normalizes grid item sizes, ensuring all breakpoints have a value. */
export const normalizeGridSizes = (sizes?: TGridSizes): Required<TGridSizes> => {
  const { xs, sm, md, lg, xl } = sizes ?? {}
  return {
    xs: xs ?? DEFAULT_GRID_ITEM_SIZES.xs ?? 12,
    sm: sm ?? xs ?? DEFAULT_GRID_ITEM_SIZES.sm ?? 6,
    md: md ?? sm ?? xs ?? DEFAULT_GRID_ITEM_SIZES.md ?? 4,
    lg: lg ?? md ?? sm ?? xs ?? DEFAULT_GRID_ITEM_SIZES.lg ?? 3,
    xl: xl ?? lg ?? md ?? sm ?? xs ?? DEFAULT_GRID_ITEM_SIZES.xl ?? 3
  }
}

/** Resolves the active breakpoint based on the container width. */
const resolveActiveBreakpoint = (width: number): keyof TGridSizes => {
  if (width >= 1536) return 'xl'
  if (width >= 1200) return 'lg'
  if (width >= 900) return 'md'
  if (width >= 600) return 'sm'
  return 'xs'
}

/** Resolves the number of grid columns based on the container width and item sizes. */
export const resolveGridColumnsFromWidth = (width: number, sizes?: TGridSizes): number => {
  const normalized = normalizeGridSizes(sizes)
  const breakpoint = resolveActiveBreakpoint(width)
  const span = normalized[breakpoint] ?? 12
  const safeSpan = Math.min(12, Math.max(1, span))
  return Math.max(1, Math.floor(12 / safeSpan))
}
